import { Suspense, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useParams } from 'react-router-dom';

import { useToast } from '@/hooks/use-toast';
import { useUser } from '@/hooks/use-user';
import { useUserTourWithUserId } from '@/hooks/use-user-tour-with-user-id';
import { loadColumnPrefs } from '@/lib/column-prefs-persistence';
import { resolveDealSignal } from '@/lib/deal-signal-resolution';
import { useDeal } from '@/lib/hooks/useDeal';
import { useDealEventStream } from '@/lib/hooks/useDealEventStream';
import { useDealItems } from '@/lib/hooks/useDealItems';
import { useGetAllCountryLabels } from '@/lib/hooks/useGetAllCountryLabels';
import { useShopifyCategoryAiFallback } from '@/lib/hooks/useShopifyCategoryAiFallback';
import { DealItemsStreamError } from '@/lib/streaming/stream-deal-items';
import { setCurrentWsDealId } from '@/lib/ws-analytics';
import NotFoundPage from '@/pages/not-found';
import {
  columnOrderState,
  DEFAULT_COLUMN_ORDER,
} from '@/states/column-order.state';
import { hiddenSortableColumnsState } from '@/states/column-visibility-user.state';
import { dealIdentifierState } from '@/states/deal-identifier.state';
import { dealSignalAtom } from '@/states/deal.state';
import { pauseCouponCsvRefetchState } from '@/states/pause-coupon-csv-refetch.state';
import {
  dealItemsLoadingState,
  dealItemsSignalState,
} from '@/states/products.state';
import { useApolloClient } from '@apollo/client/react';
import { getDefaultStore, useAtomValue, useSetAtom } from 'jotai';
import { AlertCircle, Loader2 } from 'lucide-react';

import { DealContext } from '../../lib/contexts/deal.context';
import { usePresence } from '../../lib/hooks/usePresence';

const NO_COMPANY_ASSOCIATED_MESSAGE_REGEX =
  /No company associated to deal\s+(\d+)/i;

function getNoCompanyAssociatedHubspotDealId(message?: string) {
  return message?.match(NO_COMPANY_ASSOCIATED_MESSAGE_REGEX)?.[1] ?? null;
}

function getStreamErrorDetail(error: unknown): string | undefined {
  return error instanceof DealItemsStreamError ? error.body : undefined;
}

export default function Layout() {
  const pauseCouponRefetch = useAtomValue(pauseCouponCsvRefetchState);
  const setDealIdentifier = useSetAtom(dealIdentifierState);
  const setColumnOrder = useSetAtom(columnOrderState);
  const setHiddenSortableColumns = useSetAtom(hiddenSortableColumnsState);
  const user = useUser();
  const { t } = useTranslation();
  const { toast } = useToast();
  const apolloClient = useApolloClient();

  const { dealId } = useParams<{ dealId: string }>();

  useEffect(() => {
    setCurrentWsDealId(dealId ?? null);
    return () => setCurrentWsDealId(null);
  }, [dealId]);

  usePresence(dealId!);

  useGetAllCountryLabels();

  const {
    deal,
    initialLoading: initialLoadingDeal,
    error: errorDeal,
    refetch: refetchDeal,
  } = useDeal(dealId ?? '');

  useDealItems(dealId ?? '');
  useShopifyCategoryAiFallback(dealId ?? '');
  const { initialLoading: initialLoadingDealItems, error: itemsError } =
    useAtomValue(dealItemsLoadingState);

  /**
   * Ordonnance les relectures de deal déclenchées par le canal SSE. Deux signaux
   * rapprochés peuvent résoudre dans le désordre ; sans ce compteur, la plus
   * ancienne écraserait le suivi d'état de `resolveDealSignal` avec un instantané
   * périmé, et le diff suivant rejouerait une transition déjà traitée.
   */
  const dealSignalSequenceRef = useRef(0);

  /**
   * Temps réel du deal : flux SSE « signal + refetch » (`/deals/:dealId/events`),
   * qui pilote les relectures du deal et des items.
   */
  useDealEventStream(dealId, {
    onDeal: () => {
      if (pauseCouponRefetch) return;
      getDefaultStore().set(dealSignalAtom, (count) => count + 1);
      const sequence = ++dealSignalSequenceRef.current;
      refetchDeal()
        .then(({ data }) => {
          if (sequence !== dealSignalSequenceRef.current) return;
          if (data?.deal) {
            resolveDealSignal({
              client: apolloClient,
              deal: data.deal,
              t,
              toast,
            });
          }
        })
        .catch((error: unknown) => {
          console.error('[page-layout] deal signal', error);
        });
    },
    onItems: (action, itemIds, deletedItemIds) => {
      getDefaultStore()
        .get(dealItemsSignalState)
        ?.fn(action, itemIds, deletedItemIds)
        .catch((error: unknown) => {
          console.error('[page-layout] deal items signal', error);
        });
    },
  });

  useUserTourWithUserId({
    dealId: dealId ?? '',
    dealName: deal?.hubspotInfos?.dealName ?? null,
    dealState: deal?.state,
    editionMode: deal?.editionMode,
    page: user?.isInternalUser ? 'choose' : 'brand',
    brandId: deal?.brandId,
  });

  useEffect(() => {
    const prefs = dealId ? loadColumnPrefs(dealId) : null;
    if (prefs) {
      setColumnOrder(prefs.order);
      setHiddenSortableColumns(prefs.hidden);
    } else if (dealId) {
      setColumnOrder([...DEFAULT_COLUMN_ORDER]);
      setHiddenSortableColumns([]);
    }
    setDealIdentifier(dealId ?? '');
  }, [dealId, setDealIdentifier, setColumnOrder, setHiddenSortableColumns]);

  if (initialLoadingDeal || initialLoadingDealItems) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (errorDeal || itemsError) {
    const noCompanyHubspotDealId =
      getNoCompanyAssociatedHubspotDealId(errorDeal?.message) ||
      getNoCompanyAssociatedHubspotDealId(itemsError?.message) ||
      getNoCompanyAssociatedHubspotDealId(getStreamErrorDetail(itemsError));
    const errorMessage = errorDeal?.message || itemsError?.message;

    if (noCompanyHubspotDealId) {
      if (!user?.isInternalUser) {
        return <NotFoundPage />;
      }

      return (
        <div className="flex h-screen flex-col items-center justify-center px-6">
          <div className="flex max-w-xl flex-col items-center rounded-md border border-amber-200 bg-amber-50 p-6 text-center text-amber-950 shadow-sm">
            <AlertCircle className="h-8 w-8 text-amber-600" />
            <h1 className="mt-4 text-2xl font-semibold text-black">
              {t('page.missing_hubspot_company_title')}
            </h1>
            <p className="mt-3 text-base text-amber-900">
              {t('page.missing_hubspot_company_description')}
            </p>
            <p className="mt-3 rounded-md bg-white/70 px-3 py-2 text-sm text-amber-950">
              {t('page.missing_hubspot_company_transaction_id', {
                id: noCompanyHubspotDealId,
              })}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-screen flex-col items-center justify-center">
        <AlertCircle className="h-6 w-6 text-red-500" />
        <p className="mt-4 text-lg text-black">{errorMessage}</p>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (!deal) return <NotFoundPage />;

  return (
    <DealContext.Provider value={{ deal, refetchDeal }}>
      <Suspense
        fallback={
          <div className="flex h-screen items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        }
      >
        <Outlet />
      </Suspense>
    </DealContext.Provider>
  );
}
